/**
 * estadoAtual de um item do catálogo, resolvido em runtime sobre as fontes já
 * existentes (React Query). O hub é read-only: este módulo LÊ, nunca escreve.
 *
 * `indisponivel` é honestidade, não erro: a fonte daquele item não está
 * carregada (query em voo, ou usuário sem a cap de leitura da fonte). O hub
 * renderiza «—», nunca um chute.
 */
import { resolveEscalationLimboPolicy } from '@lume/shared';
import { resolveOperatorNotificationPolicy } from '@lume/shared/push';

import type { ConfigCatalogItem } from './catalog';
import type { OperationProfileStateWire, SerializedTenantConfig } from '../config-tenant/types';

export type EstadoConfig =
  | 'ligado'
  | 'desligado'
  | 'nao-configurado'
  | 'locked'
  | 'soon'
  | 'indisponivel';

export interface FontesEstado {
  // `| undefined` explícito: tsconfig do app liga `exactOptionalPropertyTypes`
  // e `useConfigEstados` passa `query.data` (que É `T | undefined`) direto.
  readonly tenantConfig?: SerializedTenantConfig | undefined;
  readonly operationProfile?: OperationProfileStateWire | undefined;
}

/** Um resolver por item que tem estado legível; itens fora do mapa ⇒ null (sem badge). */
type Resolver = (f: FontesEstado) => EstadoConfig | null;

const RESOLVERS: Record<string, Resolver> = {
  // Admin Hub T7 (domínio Organização) — "Dados gerais" expõe 4 campos reais
  // (config-tenant/GeneralTab.tsx); `checklistAutoFulfillEnabled` é o único
  // toggle de efeito imediato/observável entre eles (os outros são seleção de
  // intervalo/visibilidade/flag LGPD, sem um "ligado" canônico único para o
  // item inteiro) — mesmo racional de usar 1 campo canônico que
  // `confirmacao-cadastral` já usa.
  geral: (f) =>
    f.tenantConfig === undefined
      ? 'indisponivel'
      : f.tenantConfig.checklistAutoFulfillEnabled
        ? 'ligado'
        : 'desligado',
  // `identidade-visual` NÃO tem resolver: sua fonte é `SerializedTenantBranding`
  // (features/admin/branding/types.ts), que não está em `FontesEstado` — só
  // `tenantConfig`/`operationProfile` estão. Wire-la exigiria tocar
  // `use-config-estados.ts` (fora do file-list da T7); ausência DECLARADA no
  // task-7-report.md, não descuido.
  'confirmacao-cadastral': (f) =>
    f.tenantConfig === undefined
      ? 'indisponivel'
      : f.tenantConfig.autoConfirmCadastralChange
        ? 'ligado'
        : 'desligado',
  // `current`, não `activeProfileId` — vide OperationProfileStateWire em
  // config-tenant/types.ts:249 ("`null` = nenhum perfil escolhido ainda").
  'perfil-de-operacao': (f) =>
    f.operationProfile === undefined
      ? 'indisponivel'
      : f.operationProfile.current != null
        ? 'ligado'
        : 'nao-configurado',
  // Admin Hub T8 (domínio IA & conduta).
  //
  // `conduta` — as 5 colunas `conduct*` são nullable (`null` = "não opinei,
  // herdo da plataforma" — docblock de `ConductTab.tsx`). Não há UM campo
  // canônico como em `geral`/`confirmacao-cadastral`; "ligado" aqui lê como
  // "o tenant opinou em pelo menos 1 dos 5 eixos" (mesmo racional de
  // `perfil-de-operacao`: presença de escolha, não um valor específico).
  conduta: (f) => {
    if (f.tenantConfig === undefined) return 'indisponivel';
    const c = f.tenantConfig;
    const algumDefinido =
      c.conductRegisterPolicy !== null ||
      c.conductGuidanceStyle !== null ||
      c.conductResponseLength !== null ||
      c.conductConfirmationStyle !== null ||
      c.conductEscalationPosture !== null;
    return algumDefinido ? 'ligado' : 'nao-configurado';
  },
  // `comportamental` — mesmo padrão de `confirmacao-cadastral`/`geral`: 1
  // master switch booleano real e canônico (`behavioralDetectionEnabled`).
  comportamental: (f) =>
    f.tenantConfig === undefined
      ? 'indisponivel'
      : f.tenantConfig.behavioralDetectionEnabled
        ? 'ligado'
        : 'desligado',
  // `acompanhamento-silencioso` — `SilentTab` expõe 2 campos (`businessHours`
  // + `outOfHoursMessage`); nenhum é "o" campo master, mas `outOfHoursMessage`
  // não-nulo é o sinal mais direto de "há algo configurado acontecendo" (o
  // auto-reply está ativo) — `businessHours: []` sozinho é ambíguo (pode ser
  // "sempre fechado" ou "tenant não configurou nenhum dia ainda").
  //
  // ★ Histórico (Admin Hub T8 fix round 1, task-8-report.md): até esse fix,
  // `catalog.ts` rotulava este item "Acompanhamento silencioso" com a
  // description da capability de PLATAFORMA `silent-monitoring` (observação
  // silenciosa de supervisor, ADR-094) — um conceito DIFERENTE do que este
  // componente faz. Corrigido: o item chama-se agora "Horário de
  // atendimento" e descreve exatamente o que `SilentTab` configura (janelas
  // de horário + auto-resposta fora delas). O resolver abaixo já lia os
  // campos REAIS de `SilentTab` mesmo antes da correção — o texto do
  // catálogo que estava errado, não este resolver. Mesma história contada
  // em `SilentTab.tsx` e `IaCondutaPage.tsx`.
  'acompanhamento-silencioso': (f) =>
    f.tenantConfig === undefined
      ? 'indisponivel'
      : f.tenantConfig.outOfHoursMessage !== null
        ? 'ligado'
        : 'desligado',
  // `modelos` NÃO tem resolver: não há um campo booleano canônico (é uma
  // superfície inteira de override por-agente + failover + escala de
  // dinamismo) e a fonte real (`useFailoverEnabled`/`useAiAgents`, em
  // `ai-config/use-ai-config.ts`) não está em `FontesEstado` — só
  // `tenantConfig`/`operationProfile` estão. Wire-la exigiria tocar
  // `use-config-estados.ts` (fora do file-list da T8); mesma ausência
  // DECLARADA que a T7 já registrou para `identidade-visual`.
  //
  // Admin Hub T9 (domínio Atendimento & fila).
  //
  // `fila-pos-transferencia` — `QueueLimboTab` expõe 2 toggles reais
  // (`ackEnabled`/`receptionistEnabled`, dentro de `escalationLimboPolicy`);
  // resolvido via `resolveEscalationLimboPolicy` (mesma resolução BE+FE única
  // que a aba já usa) para nunca reimplementar o parse do raw. "Ligado" lê
  // como "pelo menos 1 dos 2 comportamentos está ativo" — mesmo racional de
  // presença-de-escolha que `perfil-de-operacao`/`conduta` já usam.
  'fila-pos-transferencia': (f) => {
    if (f.tenantConfig === undefined) return 'indisponivel';
    const p = resolveEscalationLimboPolicy(f.tenantConfig.escalationLimboPolicy);
    return p.ackEnabled || p.receptionistEnabled ? 'ligado' : 'desligado';
  },
  // `notificacoes-do-operador` — `OperatorShiftTab` tem 1 master switch
  // booleano real e canônico (`operatorNotificationPolicy` não-nulo com
  // `enabled: true`) — mesmo padrão de `comportamental`/`confirmacao-cadastral`.
  // Resolvido via `resolveOperatorNotificationPolicy` (mesma resolução BE+FE
  // única que a aba já usa).
  'notificacoes-do-operador': (f) => {
    if (f.tenantConfig === undefined) return 'indisponivel';
    const p = resolveOperatorNotificationPolicy(f.tenantConfig.operatorNotificationPolicy);
    return p.enabled ? 'ligado' : 'desligado';
  },
  // `notificacoes` NÃO tem resolver: `soon: true` no catálogo já vence antes
  // de qualquer resolver ser consultado (ver `resolveEstado` abaixo).
  //
  // Admin Hub T10 (domínio Agenda & lembretes).
  //
  // `agenda-nativa` NÃO tem resolver: o único campo desta seção que é sempre
  // definido, `nativeSchedulingModeDefault`, é um enum de 3 valores SEM
  // default nulo — o tenant sempre "tem uma escolha" (o default de plataforma
  // é `propose_and_book`), então um resolver "ligado/desligado" sobre ele
  // seria sempre `ligado` (sinal inútil, sempre verdadeiro). O outro campo,
  // `waitlistPolicy`, é nullable mas é só METADE do que a seção expõe — um
  // resolver que ligasse o badge só a ele mentiria sobre o modo de
  // agendamento. Mesma ausência DECLARADA que `identidade-visual` (T7) e
  // `modelos` (T8) já registraram para campos sem sinal canônico único.
  //
  // `lembretes` — `RemindersTab` tem 1 master switch real e canônico
  // (`appointmentReminderPolicy` não-nulo = motor ligado, docblock do
  // componente: "`null` = motor desligado, opt-in P12") — mesmo padrão de
  // `comportamental`/`confirmacao-cadastral`. Não usa
  // `resolveAppointmentReminderPolicy` (que exige `packageType`, não
  // disponível em `FontesEstado`) porque o sinal "ligado" é sempre
  // `!== null` no raw, independente do pacote — a resolução por pacote só
  // importa para os VALORES efetivos (antecedências/modo), não para o
  // liga/desliga.
  lembretes: (f) =>
    f.tenantConfig === undefined
      ? 'indisponivel'
      : f.tenantConfig.appointmentReminderPolicy !== null
        ? 'ligado'
        : 'desligado',
  //
  // Ao implementar cada domínio (T7–T11), o dono da task ADICIONA aqui o
  // resolver dos campos que a sua seção expõe — lendo o shape REAL em
  // config-tenant/types.ts. Item sem resolver não exibe badge (null).
};

export function resolveEstado(
  item: ConfigCatalogItem,
  fontes: FontesEstado,
  capabilities: readonly string[],
): EstadoConfig | null {
  if (item.soon === true) return 'soon';
  if (!capabilities.includes(item.capability)) return 'locked';
  const resolver = RESOLVERS[item.id];
  return resolver != null ? resolver(fontes) : null;
}
